/**
 * GapOutput — renders the gap_analysis step:
 *   composite_gap_score (large) + severity Chip, the top critical gaps, and a
 *   set of domain-gap score bars. Assessment/recommendation prose plus the
 *   narrative_markdown trail below.
 */

import { Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { ResumeStepData } from "../../../api/client";
import LessonMarkdown from "../../LessonMarkdown";
import { BulletSection, ScoreBar, SectionLabel } from "./shared";

export default function GapOutput({ step }: { step: ResumeStepData }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const o = step.output ?? {};
  // Internal model tracks gap (higher = worse); the candidate sees READINESS = 100 − gap.
  const gap = o.composite_gap_score;
  const readiness = typeof gap === "number" ? Math.round(100 - gap) : undefined;
  const severity: string | undefined = o.severity;
  const domainGaps: any[] = o.domain_gaps ?? [];

  /** Map a severity string to a chip color (defaults to coral via theme). */
  function severityColor(sev?: string): string {
    switch ((sev ?? "").toLowerCase()) {
      case "low":
      case "minor":
        return theme.palette.success.main;
      case "high":
      case "severe":
      case "critical":
        return theme.palette.error.dark;
      default:
        return theme.palette.primary.main;
    }
  }

  const sevColor = severityColor(severity);

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {typeof readiness === "number" && (
          <Box
            sx={{
              minWidth: 96,
              px: 2.5,
              py: 1.5,
              borderRadius: 3,
              textAlign: "center",
              bgcolor: alpha(theme.palette.primary.light, 0.08),
            }}
          >
            <Typography sx={{ fontSize: 40, fontWeight: 500, lineHeight: 1, color: theme.palette.primary.main }}>
              {readiness}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, color: alpha(theme.palette.primary.main, 0.7) }}>
              {t("GapOutput.readinessLabel")}
            </Typography>
          </Box>
        )}
        {severity && (
          <Chip
            label={severity}
            sx={{
              fontWeight: 500,
              bgcolor: alpha(sevColor, 0.12),
              color: sevColor,
            }}
          />
        )}
      </Stack>

      <BulletSection label={t("GapOutput.whereToFocus")} items={o.top_critical_gaps} color={sevColor} />

      {domainGaps.length > 0 && (
        <Box>
          <SectionLabel>{t("GapOutput.domainReadiness")}</SectionLabel>
          <Stack spacing={1.25} mt={1}>
            {domainGaps.map((d, i) => (
              <ScoreBar
                key={`${d?.domain ?? d?.label ?? i}`}
                label={d?.label ?? d?.domain ?? t("GapOutput.domainFallback", { index: i + 1 })}
                value={100 - Number(d?.gap_score ?? 0)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {o.assessment && (
        <Box>
          <SectionLabel>{t("GapOutput.assessment")}</SectionLabel>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {o.assessment}
          </Typography>
        </Box>
      )}

      {o.recommendation && (
        <Box>
          <SectionLabel>{t("GapOutput.pathForward")}</SectionLabel>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {o.recommendation}
          </Typography>
        </Box>
      )}

      <LessonMarkdown>{step.narrative_markdown ?? ""}</LessonMarkdown>
    </Stack>
  );
}
